/**
 * Biometric (Face ID / Touch ID) unlock.
 *
 * Flow:
 *  1. After a successful password sign-in on a native device, we persist the
 *     Supabase *refresh token* in the iOS Keychain (via SecureStorage), gated
 *     behind the device passcode. See `enableBiometricUnlock`.
 *  2. On next cold start, if a stored token exists and the device supports
 *     biometrics, we prompt with Face ID; on success we exchange the refresh
 *     token for a live session via `supabase.auth.setSession`. See
 *     `tryBiometricUnlock`.
 *
 * Everything here is a no-op / returns `false` on the web, so callers can use
 * it unconditionally.
 */

import { BiometricAuth, BiometryError } from '@aparajita/capacitor-biometric-auth';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { supabase } from '@/lib/supabase';
import { isNativePlatform } from './platform';

const REFRESH_TOKEN_KEY = 'projexhq.biometric.refreshToken';
const ENABLED_KEY = 'projexhq.biometric.enabled';

/** Whether this device has enrolled biometrics we can use. */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!isNativePlatform()) return false;
  try {
    const info = await BiometricAuth.checkBiometry();
    return info.isAvailable;
  } catch {
    return false;
  }
}

/** Human label for the current biometry, e.g. "Face ID" / "Touch ID". */
export async function biometryLabel(): Promise<string> {
  if (!isNativePlatform()) return 'Biometrics';
  try {
    const info = await BiometricAuth.checkBiometry();
    return niceName(info.biometryType);
  } catch {
    return 'Biometrics';
  }
}

function niceName(type: number): string {
  // BiometryType: 0 none, 1 touchId, 2 faceId, 3 fingerprint, 4 faceAuth, 5 iris
  switch (type) {
    case 1:
      return 'Touch ID';
    case 2:
      return 'Face ID';
    default:
      return 'Biometrics';
  }
}

/** True if the user has previously opted in and we hold a stored token. */
export async function isBiometricUnlockEnabled(): Promise<boolean> {
  if (!isNativePlatform()) return false;
  try {
    const enabled = await SecureStorage.get(ENABLED_KEY);
    return enabled === true || enabled === 'true';
  } catch {
    return false;
  }
}

/**
 * Persist the current session's refresh token behind biometrics. Call this
 * right after a successful password sign-in, once the user opts in.
 */
export async function enableBiometricUnlock(): Promise<boolean> {
  if (!isNativePlatform() || !supabase) return false;
  const { data } = await supabase.auth.getSession();
  const refreshToken = data.session?.refresh_token;
  if (!refreshToken) return false;

  await SecureStorage.set(REFRESH_TOKEN_KEY, refreshToken);
  await SecureStorage.set(ENABLED_KEY, 'true');
  return true;
}

/** Forget the stored token (called on sign-out or when the user opts out). */
export async function disableBiometricUnlock(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await SecureStorage.remove(REFRESH_TOKEN_KEY);
    await SecureStorage.remove(ENABLED_KEY);
  } catch {
    /* ignore */
  }
}

export type BiometricUnlockResult =
  | { ok: true }
  | { ok: false; reason: 'unavailable' | 'cancelled' | 'no-token' | 'error'; message?: string };

/**
 * Prompt Face ID / Touch ID and, on success, restore the Supabase session
 * from the stored refresh token. Returns a discriminated result so the UI can
 * distinguish a user cancel from a hard failure.
 */
export async function tryBiometricUnlock(): Promise<BiometricUnlockResult> {
  if (!isNativePlatform() || !supabase) return { ok: false, reason: 'unavailable' };

  if (!(await isBiometricUnlockEnabled())) return { ok: false, reason: 'no-token' };

  let refreshToken: string | null | undefined;
  try {
    const stored = await SecureStorage.get(REFRESH_TOKEN_KEY);
    refreshToken = typeof stored === 'string' ? stored : null;
  } catch {
    return { ok: false, reason: 'error', message: 'Secure storage unavailable' };
  }
  if (!refreshToken) return { ok: false, reason: 'no-token' };

  try {
    await BiometricAuth.authenticate({
      reason: 'Unlock ProjexHQ',
      cancelTitle: 'Use password',
      allowDeviceCredential: false,
      iosFallbackTitle: 'Use password',
    });
  } catch (err) {
    if (err instanceof BiometryError) {
      // User cancelled or auth failed — fall back to password entry.
      return { ok: false, reason: 'cancelled', message: err.message };
    }
    return { ok: false, reason: 'error', message: String(err) };
  }

  // Biometry passed — restore the Supabase session.
  const { error } = await supabase.auth.setSession({
    access_token: '',
    refresh_token: refreshToken,
  });
  if (error) {
    // Stored token is stale/revoked — clear it so we stop prompting.
    await disableBiometricUnlock();
    return { ok: false, reason: 'error', message: error.message };
  }
  return { ok: true };
}
